import { useState } from 'react';
import { ChevronDownIcon, VideoCameraIcon } from '@heroicons/react/24/outline';
import type { GeminiErrorObservation } from '@/types/api';

// ─── helpers ─────────────────────────────────────────────────────────────────

/** Convert snake_case to Title Case, e.g. "axis_deviation" → "Axis Deviation" */
function formatErrorType(raw: string): string {
  return raw
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

type Severity = GeminiErrorObservation['severity'];

interface SeverityStyle {
  badge: string;
  border: string;
  bg: string;
  label: string;
}

const SEVERITY_STYLES: Record<Severity, SeverityStyle> = {
  critical: {
    badge: 'bg-[#F05D5E]/20 text-[#F05D5E] border-[#F05D5E]/40',
    border: 'border-[#F05D5E]/30',
    bg: 'bg-[#131920]',
    label: 'Critical',
  },
  significant: {
    badge: 'bg-[#F5A524]/20 text-[#F5A524] border-[#F5A524]/40',
    border: 'border-[#F5A524]/30',
    bg: 'bg-[#131920]',
    label: 'Significant',
  },
  minor: {
    badge: 'bg-gray-500/20 text-gray-400 border-gray-500/40',
    border: 'border-gray-600/30',
    bg: 'bg-[#131920]',
    label: 'Minor',
  },
};

// ─── GeminiObservationCard ────────────────────────────────────────────────────

interface GeminiObservationCardProps {
  observation: GeminiErrorObservation;
}

function GeminiObservationCard({ observation }: GeminiObservationCardProps) {
  const [calloutsExpanded, setCalloutsExpanded] = useState(false);
  const styles = SEVERITY_STYLES[observation.severity] ?? SEVERITY_STYLES.minor;
  const hasCallouts = observation.frame_callouts && observation.frame_callouts.length > 0;

  return (
    <div
      className={`${styles.bg} ${styles.border} border rounded-lg p-4 space-y-3`}
    >
      {/* Header row: error type + severity badge */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <span className="font-semibold text-white text-sm">
          {formatErrorType(observation.error_type)}
        </span>
        <span
          className={`inline-flex items-center px-2 py-0.5 text-xs font-medium rounded-full border ${styles.badge}`}
        >
          {styles.label}
        </span>
      </div>

      {/* Visual description */}
      <p className="text-gray-300 text-sm leading-relaxed">
        {observation.visual_description}
      </p>

      {/* Coaching suggestion */}
      {observation.coaching_suggestion && (
        <div className="flex items-start gap-2 bg-white/5 rounded-md px-3 py-2">
          <span className="text-gray-400 text-xs font-medium shrink-0 mt-0.5">Suggestion</span>
          <p className="text-gray-300 text-xs leading-relaxed">{observation.coaching_suggestion}</p>
        </div>
      )}

      {/* Expandable frame callouts */}
      {hasCallouts && (
        <div>
          <button
            type="button"
            onClick={() => setCalloutsExpanded((prev) => !prev)}
            className="flex items-center gap-1.5 text-xs text-gray-400 hover:text-gray-200 transition-colors"
            aria-expanded={calloutsExpanded}
          >
            <ChevronDownIcon
              className={`h-3.5 w-3.5 transition-transform duration-200 ${calloutsExpanded ? 'rotate-180' : ''}`}
            />
            {calloutsExpanded ? 'Hide' : 'Show'} frame callouts (
            {observation.frame_callouts.length})
          </button>

          {calloutsExpanded && (
            <ul className="mt-2 space-y-1.5">
              {observation.frame_callouts.map((callout, idx) => (
                <li
                  key={idx}
                  className="flex items-start gap-2 text-xs text-gray-400 bg-black/20 rounded px-2.5 py-1.5"
                >
                  <span className="font-mono text-gray-500 shrink-0 mt-0.5">
                    f{callout.frame_index}
                  </span>
                  <span className="text-gray-300">{callout.observation}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}

// ─── GeminiObservationSection ─────────────────────────────────────────────────

interface GeminiObservationSectionProps {
  observations: GeminiErrorObservation[];
}

/**
 * Renders a list of Gemini Vision error observations produced during evaluation.
 * Returns null when the observations array is empty, so callers may render this
 * unconditionally after guarding for existence.
 */
function GeminiObservationSection({ observations }: GeminiObservationSectionProps) {
  if (!observations || observations.length === 0) {
    return null;
  }

  return (
    <section aria-label="Visual Analysis" className="space-y-3">
      {/* Section header */}
      <div className="flex items-center gap-2">
        <VideoCameraIcon className="h-5 w-5 text-gray-400 shrink-0" />
        <div>
          <h3 className="text-sm font-semibold text-white">Visual Analysis</h3>
          <p className="text-xs text-gray-400">AI-powered video frame analysis</p>
        </div>
      </div>

      {/* Cards */}
      <div className="space-y-3">
        {observations.map((obs, i) => (
          <GeminiObservationCard key={i} observation={obs} />
        ))}
      </div>
    </section>
  );
}

export default GeminiObservationSection;
